import classNames from 'classnames'
import * as React from 'react'

import { isMobile } from '../../../app/shared/isMobile'
import { getDefaultValueGetter } from '../../hooks/use-sort-data'
import { columnMap } from '../../modules/portfolio/constants/columns'
import {
  ColumnId,
  PortfolioGroup,
  PortfolioSymbolType,
  RowKind,
  TableColumnDefinition,
} from '../../modules/portfolio/types'
import { getColumnsByKeys } from '../../modules/portfolio/utils'
import { SortableValue } from '../../types'
import { SortDirection, Table, TableCell, TableHead, TableHeaderSortable, TableRow, useTableSort } from '../Table'

interface PortfolioWatchlistTableProps {
  data: PortfolioGroup[]
  selectedPortfolio: number
  handleTickerSelect: (ticker: string) => void
  chartTicker?: string
  isSelectClosed: boolean
  tableOrder: ColumnId
  handleOrderChange: (column: ColumnId) => void
}

const visibleColumns = getColumnsByKeys([
  ColumnId.Ticker,
  ColumnId.LastClose,
  ColumnId.LastChangePct,
  ColumnId.LastChange,
])
const sortableColumns = [columnMap[ColumnId.Order], ...visibleColumns]

const IS_MOBILE = isMobile()

function isElementOverflowing(el: HTMLElement, containerElement?: HTMLDivElement) {
  if (!containerElement) return false

  const element = el.getBoundingClientRect()
  const container = containerElement.getBoundingClientRect()

  return element.top >= container.top && element.bottom <= container.bottom
}

function valueGetter(column: TableColumnDefinition) {
  const columnValueGetter = column.getValueForSort

  if (typeof columnValueGetter === 'undefined') return getDefaultValueGetter(column)

  return (row: PortfolioGroup): SortableValue => columnValueGetter({ kind: RowKind.Group, row }) ?? null
}

export function PortfolioWatchlistTable({
  data,
  handleTickerSelect,
  chartTicker,
  selectedPortfolio,
  isSelectClosed,
  tableOrder,
  handleOrderChange,
}: PortfolioWatchlistTableProps) {
  const stockRows = React.useMemo(() => data.filter((group) => group.type !== PortfolioSymbolType.Cash), [data])
  const tableSort = useTableSort({ data: stockRows, columns: sortableColumns, valueGetter })
  const [selectedRow, setSelectedRow] = React.useState<string>()
  const [containerElement, setContainerElement] = React.useState<HTMLDivElement>()

  React.useEffect(() => {
    const element = document.querySelector('[data-js="portfolio-watchlist-table-sidebar-container"]')
    if (element) setContainerElement(element as HTMLDivElement)
  }, [])

  React.useEffect(() => {
    const rowElement = document.querySelector(`[data-ticker="${chartTicker}"]`) as HTMLElement
    if (isSelectClosed && rowElement !== document.activeElement && data.some(({ ticker }) => chartTicker === ticker)) {
      setSelectedRow(chartTicker)
      rowElement?.focus()
    }
    // We want to trigger this only on portfolio change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio, isSelectClosed, chartTicker])

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLTableRowElement>, index: number) => {
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault()
    } else {
      return
    }

    const prevSibling = ev.currentTarget.previousElementSibling as HTMLElement | null
    const nextSibling = ev.currentTarget.nextElementSibling as HTMLElement | null
    const firstElement = ev.currentTarget.parentElement?.firstElementChild as HTMLElement | null
    const lastElement = ev.currentTarget.parentElement?.lastElementChild as HTMLElement | null

    if (ev.key === 'ArrowDown') {
      nextSibling?.focus({ preventScroll: true })
      if (!nextSibling) firstElement?.focus({ preventScroll: true })
      const el = document.activeElement as HTMLElement | null
      if (el && !isElementOverflowing(el, containerElement)) {
        document.activeElement?.scrollIntoView(false)
      }
    }
    if (ev.key === 'ArrowUp') {
      prevSibling?.focus({ preventScroll: true })
      if (!prevSibling) lastElement?.focus({ preventScroll: true })
      const el = document.activeElement as HTMLElement | null
      if (el && !isElementOverflowing(el, containerElement)) {
        document.activeElement?.scrollIntoView(index <= 1 ? false : true)
      }
    }

    const ticker = (document.activeElement as HTMLElement | null)?.dataset.ticker

    if (ticker) {
      setSelectedRow(ticker)
    }
  }

  const handleKeyUp = React.useCallback(
    (ev: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
        ev.preventDefault()
        const ticker = (document.activeElement as HTMLElement | null)?.dataset.ticker
        if (ticker) {
          handleTickerSelect(ticker)
        }
      }
    },
    [handleTickerSelect]
  )

  const handleTableMouseMove = (ev: React.MouseEvent<HTMLTableRowElement>) => {
    if (!selectedRow) {
      ev.currentTarget.focus({ preventScroll: true })
    }
  }

  const handleSortChange = React.useCallback(
    (column: TableColumnDefinition) => {
      let newSort = column
      if (tableSort.sort.column.id === column.id && tableSort.sort.direction === SortDirection.Descending) {
        newSort = sortableColumns[0]
      }

      tableSort.changeSort(newSort)
      handleOrderChange(newSort.id)
    },
    [handleOrderChange, tableSort]
  )

  // Update sort when props change (used for watchlist sidebar header)
  React.useEffect(() => {
    if (tableOrder !== tableSort.sort.column.id) tableSort.changeSort(columnMap[tableOrder])
    // Only run this on props change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableOrder])

  return (
    <Table className="border-separate">
      <TableHead className="sticky -top-2 bg-white dark:bg-gray-800">
        {visibleColumns.map((column) => (
          <TableHeaderSortable
            className="mobile:h-8"
            key={column.id}
            align={column.align}
            isActive={tableSort.sort.column.id === column.id}
            direction={tableSort.sort.direction}
            onClick={() => handleSortChange(column)}
          >
            {column.name}
          </TableHeaderSortable>
        ))}
      </TableHead>
      <tbody>
        {tableSort.data.map((entry, index) => (
          <TableRow
            data-ticker={entry.ticker}
            tabIndex={index}
            key={`${entry.ticker}-${index}`}
            className={classNames('cursor-pointer outline-none', {
              'h-6': !IS_MOBILE,
              'h-8': IS_MOBILE,
            })}
            onClick={() => {
              setSelectedRow(entry.ticker)
              handleTickerSelect(entry.ticker)
            }}
            onMouseMove={handleTableMouseMove}
            onKeyDown={(ev) => handleKeyDown(ev, index)}
            onKeyUp={handleKeyUp}
          >
            {visibleColumns.map((column) => (
              <TableCell
                className={classNames('first:rounded-l-md last:rounded-r-md ', {
                  'bg-blue-100 dark:bg-gray-700': selectedRow === entry.ticker,
                  'group-hover:bg-blue-50 dark:group-hover:bg-gray-750': selectedRow !== entry.ticker,
                })}
                key={column.id}
                align={column.align}
              >
                {column.renderColumn({ kind: RowKind.Watchlist, row: entry, index })}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </tbody>
    </Table>
  )
}
